import React, { createContext, useContext, useEffect } from 'react';

/**
 * ThemeContext — LIGHT MODE ONLY.
 *
 * Dark mode has been removed from BidSignalIQ. This provider exists solely
 * to keep the existing `useTheme()` API stable for consumers that still
 * import it. It always reports light and silently no-ops toggle calls.
 *
 * The provider also strips any `.dark` class that might be on the root
 * element (e.g. from an older localStorage entry) and clears the stored
 * preference on mount, so returning users are pinned to light forever.
 */
const ThemeContext = createContext(null);

const NOOP = () => {};

export const ThemeProvider = ({ children }) => {
  useEffect(() => {
    try {
      const root = window.document.documentElement;
      root.classList.remove('dark');
      root.classList.add('light');
      // Force light color-scheme so form controls / scrollbars follow suit.
      root.style.colorScheme = 'light';
      // Wipe any historical preference — no more theme persistence.
      localStorage.removeItem('bidsignaliq-theme');
    } catch (_) { /* SSR / no-window safety */ }
  }, []);

  const value = {
    theme: 'light',
    isDark: false,
    isLight: true,
    toggleTheme: NOOP,
    setLightTheme: NOOP,
    setDarkTheme: NOOP,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  // Safe fallback — never throws even if the provider is missing (defensive
  // now that dark mode is dead and consumers may be stripped out over time).
  return useContext(ThemeContext) || {
    theme: 'light', isDark: false, isLight: true,
    toggleTheme: NOOP, setLightTheme: NOOP, setDarkTheme: NOOP,
  };
};

export default ThemeContext;
